import React, { useState } from 'react';
import {
  Typography,
  Box,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
} from '@mui/material';
import {
  HelpOutline as HelpOutlineIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  TableChart as TableChartIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import ProductionCharts from '../charts/ProductionCharts';
import FullScreenTable from '../tables/FullScreenTable';
import UpgradeRequestDialog from '../UpgradeRequestDialog';
import logger from '../../utils/logger';

const ProductionTab = ({
  searchData,
  getCurrentTableCounts,
  getCurrentTableData,
  getCurrentTableType,
  productionTableSort,
  handleProductionTableSort,
  resetProductionTableSort,
  hiddenProductionLines,
  setHiddenProductionLines,
  resetHiddenProductionLines,
  hiddenFlaringLines,
  setHiddenFlaringLines,
  resetHiddenFlaringLines,
  hiddenFuelGasLines,
  setHiddenFuelGasLines,
  resetHiddenFuelGasLines,
  tierInfo,
}) => {
  // Local state for operator names popup
  const [operatorNamesDialogOpen, setOperatorNamesDialogOpen] = useState(false);
  const [fullScreenTableOpen, setFullScreenTableOpen] = useState(false);
  const [aboutDialogOpen, setAboutDialogOpen] = useState(false);
  // Local state for upgrade dialog
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  // Don't render if not production tab or no data
  if (getCurrentTableType() !== 'production' || !getCurrentTableData() || getCurrentTableData().length === 0) {
    return null;
  }

  const productionData = getCurrentTableData();
  
  // Calculate unique operator names for this tab only
  const getTabOperatorNames = () => {
    if (!productionData || productionData.length === 0) return [];
    
    const operatorNames = new Set();
    productionData.forEach(record => {
      const operator = record.operator_name || record['Operator Name'] || record.operator;
      if (operator && operator.toString().trim() !== '') {
        operatorNames.add(operator.toString().trim());
      }
    });
    
    return Array.from(operatorNames).sort();
  };
  
  // Calculate totals and averages
  const totalOil = productionData.reduce((sum, record) => {
    const oil = parseFloat(record.oil_bbl) || 0;
    return sum + oil;
  }, 0);
  
  const totalGas = productionData.reduce((sum, record) => {
    const gas = parseFloat(record.gas_mcf) || 0;
    return sum + gas;
  }, 0);
  
  const totalFlaring = productionData.reduce((sum, record) => {
    const flaring = parseFloat(record.flare_gas_mcf) || 0;
    return sum + flaring;
  }, 0);
  
  const totalFuelGas = productionData.reduce((sum, record) => {
    const fuelGas = parseFloat(record.fuel_gas_mcf) || 0;
    return sum + fuelGas;
  }, 0);
  
  // Calculate unique production months and total days
  const uniqueMonths = new Set(productionData.map(record => record.production_month));
  const totalDays = Array.from(uniqueMonths).reduce((sum, month) => {
    // Find first record for this month to get its days_month
    const firstRecord = productionData.find(record => record.production_month === month);
    return sum + (parseFloat(firstRecord?.days_month) || 0);
  }, 0);
  
  // Calculate daily averages
  const avgOilPerDay = totalDays > 0 ? totalOil / totalDays : 0;
  const avgGasPerDay = totalDays > 0 ? totalGas / totalDays : 0;
  const avgFlaringPerDay = totalDays > 0 ? totalFlaring / totalDays : 0;
  const avgFuelGasPerDay = totalDays > 0 ? totalFuelGas / totalDays : 0;
  
  // Calculate intensities (percentages)
  const flaringIntensity = totalGas > 0 ? (totalFlaring / totalGas) * 100 : 0;
  const fuelGasIntensity = totalGas > 0 ? (totalFuelGas / totalGas) * 100 : 0;
  
  // Calculate date range for production data
  let productionDateRangeText = '';
  if (uniqueMonths.size > 0) {
    const sortedMonths = Array.from(uniqueMonths).sort();
    const firstMonth = sortedMonths[0];
    const lastMonth = sortedMonths[sortedMonths.length - 1];
    
    // Format months as "Mon YYYY"
    const formatMonth = (monthStr) => {
      if (monthStr && monthStr.includes('-')) {
        const [year, month] = monthStr.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1);
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      }
      return monthStr;
    };
    
    if (firstMonth === lastMonth) {
      productionDateRangeText = ` (${formatMonth(firstMonth)})`;
    } else {
      productionDateRangeText = `, with data spanning from ${formatMonth(firstMonth)} to ${formatMonth(lastMonth)}`;
    }
  }

  return (
    <>
      {/* Production Findings Card */}
      <Card elevation={0} sx={{ mb: 3, border: 1, borderColor: 'divider' }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <AssessmentIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
            <Typography variant="h5" component="h2">
              Production
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 'auto' }}>
              <Tooltip title="About Production" arrow>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setAboutDialogOpen(true)}
                >
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="View Raw Data" arrow>
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setFullScreenTableOpen(true)}
                    disabled={tierInfo && !tierInfo.can_view_raw_data}
                    sx={{
                      opacity: (tierInfo && !tierInfo.can_view_raw_data) ? 0.5 : 1,
                      cursor: (tierInfo && !tierInfo.can_view_raw_data) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <TableChartIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
          
          {/* Content */}
          <Box>
            {/* Unique Operator Names */}
            <Box sx={{ mb: 2, textAlign: 'left' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>
                  Unique Operator Names — {getTabOperatorNames().length.toLocaleString()}
                </Typography>
                <Tooltip title="View all unique operator names" arrow>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setOperatorNamesDialogOpen(true)}
                  >
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            
            {/* Divider */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1, mt: 2 }} />
            
            {/* Findings Content */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 3 }}>
                The production data review identified average reported production of{' '}
                <strong>{avgOilPerDay.toLocaleString(undefined, { maximumFractionDigits: 0 })} bbl/d</strong> of oil and{' '}
                <strong>{avgGasPerDay.toLocaleString(undefined, { maximumFractionDigits: 0 })} Mcf/d</strong> of gas
                from the subject assets{productionDateRangeText}.
              </Typography>

              <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 2 }}>
                The review further identified the following gas disposition details associated with the subject assets{productionDateRangeText ? `, over the same period` : ''}:
              </Typography>

              <Box sx={{ ml: 2, mb: 2 }}>
                <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 1 }}>
                  <strong>GAS FLARING</strong> rate of{' '}
                  <strong>{avgFlaringPerDay.toLocaleString(undefined, { maximumFractionDigits: 0 })} Mcf/d</strong> and intensity of{' '}
                  <strong>{flaringIntensity.toFixed(2)}%</strong> of gas produced.
                </Typography>

                <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 3 }}>
                  <strong>FUEL GAS USE</strong> rate of{' '}
                  <strong>{avgFuelGasPerDay.toLocaleString(undefined, { maximumFractionDigits: 0 })} Mcf/d</strong> and intensity of{' '}
                  <strong>{fuelGasIntensity.toFixed(2)}%</strong> of gas produced.
                </Typography>
              </Box>

              {/* Analytics Scope Notice */}
              {getCurrentTableData().length < getCurrentTableCounts() && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Note:</strong> Analytics are based on {getCurrentTableData().length.toLocaleString()} of {getCurrentTableCounts().toLocaleString()} identified records 
                    (limited to first {getCurrentTableData().length.toLocaleString()} rows).
                  </Typography>
                </Alert>
              )}
            </Box>
          </Box>
          
          {tierInfo && !tierInfo.can_view_raw_data && (
            <Typography variant="body2" color="primary.main" sx={{ 
              mt: 2, 
              mb: 1, 
              fontWeight: 500 
            }}>
              💡 <button 
                onClick={() => setUpgradeDialogOpen(true)}
                style={{ 
                  color: 'inherit', 
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit'
                }}
              >
                Upgrade to Pro to view raw data tables
              </button>
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Production Charts */}
      <ProductionCharts
        productionData={getCurrentTableData()}
        onSortProductionMonthly={handleProductionTableSort}
        productionMonthlySortField={productionTableSort.field}
        productionMonthlySortDirection={productionTableSort.direction}
        onResetProductionMonthly={resetProductionTableSort}
        hiddenProductionLines={hiddenProductionLines}
        setHiddenProductionLines={setHiddenProductionLines}
        resetHiddenProductionLines={resetHiddenProductionLines}
        hiddenFlaringLines={hiddenFlaringLines}
        setHiddenFlaringLines={setHiddenFlaringLines}
        resetHiddenFlaringLines={resetHiddenFlaringLines}
        hiddenFuelGasLines={hiddenFuelGasLines}
        setHiddenFuelGasLines={setHiddenFuelGasLines}
        resetHiddenFuelGasLines={resetHiddenFuelGasLines}
      />

      {/* About Dialog */}
      <Dialog
        open={aboutDialogOpen}
        onClose={() => setAboutDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          About Production
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Typography paragraph>
              Oil & gas production and gas disposition data in this dataset is aggregated from regulatory filings for all reporting producers of crude oil, casinghead gas, gas well gas, and condensate.
            </Typography>
            <Typography paragraph>
              Oil and gas production in the State of Texas is reported by lease rather than by individual well. In the case of an oil lease, reported monthly production includes production from all of the wells on the lease. A single oil lease may include numerous oil wells, while gas leases contain only one gas well per lease.
            </Typography>
            <Typography paragraph>
              Operators are required to submit production reports by the last day of the month for the previous month. For example, July production reports are due at the end of August. However, due to this reporting delay, there is at least a two-month lag time for online production information.
            </Typography>
            <Typography paragraph>
              The production information is sourced directly from the reports submitted by the operators. However, production reports are a snapshot in time, and the production information may change and be updated as revised, corrected or delinquent production reports from operators are received. 
              Historically, production records are considered substantially complete after about six months.
            </Typography>
            <Typography paragraph sx={{ fontStyle: 'italic' }}>
              <strong>Note:</strong> The oil production data in this dataset combines the crude oil produced from oil wells/leases and condensate (liquid hydrocarbons) produced from gas wells as reported by operators.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAboutDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Operator Names Dialog */}
      <Dialog
        open={operatorNamesDialogOpen}
        onClose={() => setOperatorNamesDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Unique Operator Names
            </Typography>
            <IconButton
              onClick={() => setOperatorNamesDialogOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing unique operator names from current search results ({getCurrentTableData().length.toLocaleString()} records)
          </Typography>
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {getTabOperatorNames().length > 0 ? (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Total: {getTabOperatorNames().length.toLocaleString()} unique operators</strong>
                </Typography>
                <List dense>
                  {getTabOperatorNames().map((operator, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemText primary={operator} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : (
              <Typography>No operator data available</Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Full Screen Table Dialog */}
      <FullScreenTable
        open={fullScreenTableOpen}
        onClose={() => setFullScreenTableOpen(false)}
        tableType="production"
        data={productionData}
        allColumns={productionData.length > 0 ? Object.keys(productionData[0]) : []}
        defaultColumns={['operator_name', 'lease_name', 'production_month', 'oil_bbl', 'gas_mcf']}
        totalCount={getCurrentTableCounts()}
        rowLimit={tierInfo?.table_limits?.production || 2500}
        onReset={() => {
          // Reset any production-specific state if needed
          logger.debug('Production table reset');
        }}
      />
      
      {/* Upgrade Request Dialog */}
      <UpgradeRequestDialog 
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
      />
    </>
  );
};

export default ProductionTab;


